import styled from 'styled-components';
import {isEmpty, isNumber} from 'lodash';

import {PaymentMethodTypes} from '~/shared/consts/paymentConsts';
import HomePageArrow from '~/assets/images/svgIconsAsText/basic-arrow-down.svg?react';
import {getLocalizationService} from '~/shared/services/localisationService';
import {mapDebitTransferStatus} from '~/shared/consts/userTransactionsConsts';
import {useMenuList} from '~/common/hooks/UserTransactions';

import SideMenu from '../SideMenu';
import Price from '../Price';
import CancelButton from '../CancelButton';

import {ContentCell, BorderHider, Text} from './styled';

export const Arrow = styled(HomePageArrow)`
  ${({$facedown}) => $facedown && 'transform: rotate(180deg)'};
  transition: transform 0.15s ease-in-out;
`;

const CellRenderer = ({
  headerName,
  rowData,
  toggleRow,
  cellData,
  dataKey,
  openRows,
  boldAllPrices,
  useTranslate: shouldUseTranslate,
  debitSentRequestsTable: isDebitSentRequestsTable,
  isTransactionTable,
  menuEvents,
  onCancelButtonClick,
  cellWidth,
  isFooterCell,
  skeletonComponent,
  hideToggleArrow,
  onMenuClick,
  openedRowMenuId,
}) => {
  const isParentRow = rowData.id;
  const isMenuCell = headerName === 'menu' && isParentRow;

  const {t} = getLocalizationService();
  const menuList = useMenuList(rowData, menuEvents);

  if (!rowData && skeletonComponent) {
    // Show Skeleton
    return (
      <ContentCell cellWidth={cellWidth}>
        {dataKey === 'menu' || dataKey === 'collapse' || dataKey === 'emptyCell' ? <div /> : skeletonComponent}
      </ContentCell>
    );
  }

  if (isMenuCell) {
    return (
      <ContentCell cellWidth={cellWidth} center>
        <SideMenu
          onClick={onMenuClick}
          order={rowData}
          menuList={menuList}
          openedRowMenuId={openedRowMenuId}
          {...menuEvents}
        />
      </ContentCell>
    );
  }

  const isCollapseButtonCell = headerName === 'collapse' && isParentRow;
  const isCurrentRowCollapsed = openRows && openRows.includes(rowData.id);
  if (isCollapseButtonCell) {
    return (
      <ContentCell cellWidth={cellWidth} center>
        {!hideToggleArrow && <Arrow $facedown={isCurrentRowCollapsed} />}
      </ContentCell>
    );
  }

  const isChildCollapseFirstAndLastCell = (headerName === 'collapse' || headerName === 'menu') && !rowData.marked;
  if (isChildCollapseFirstAndLastCell) {
    return (
      <ContentCell flex>
        <BorderHider headerName={headerName} />
      </ContentCell>
    );
  }

  const additionalTextFields = ['restaurantName', 'subsidizedMeals', 'nettMeals'];

  const isPaymentOrDishesLabelCells = !isParentRow && rowData.dateAndTime && !additionalTextFields.includes(dataKey);
  const boldText = rowData.marked || isPaymentOrDishesLabelCells || (dataKey === 'restaurantName' && isParentRow);
  const spaceCells = !isParentRow && dataKey === 'dateAndTime' && !rowData.marked && !isDebitSentRequestsTable;
  const shouldShowCancelButton = isDebitSentRequestsTable && dataKey === 'cancelletion';
  const isFullWidthCell = rowData.isCommentRow;
  const isCenteredText = rowData?.centered && dataKey !== 'dateAndTime';

  const extraDataKeysPrices = ['personalGuestTotal', 'businessMeetingGuestTotal', 'usageBruto', 'usageNet'];
  return (
    <ContentCell
      cellWidth={cellWidth}
      isFullWidthCell={isFullWidthCell}
      marked={rowData.marked}
      data-test-id={`cell_${dataKey}`}
      onClick={() => (isParentRow && isTransactionTable ? toggleRow({rowData}) : null)}
    >
      {(dataKey === 'total' ||
        (extraDataKeysPrices.includes(dataKey) &&
          rowData.paymentMethod === PaymentMethodTypes.MONEY_CARD &&
          !isFooterCell)) &&
      isEmpty(cellData) &&
      !isFullWidthCell ? (
        <Price
          isBold={(dataKey === 'total') || rowData.marked || rowData.isTotalAmountRow || (boldAllPrices)}
          price={cellData}
          isCanceled={rowData.isCanceled}
          t={t}
        />
      ) : shouldShowCancelButton ? (
        <CancelButton
          mapDebitTransferStatus={mapDebitTransferStatus}
          transferData={rowData}
          onCancelClick={onCancelButtonClick}
        />
      ) : (dataKey === 'prepaidBalance' || dataKey === 'orderPrice') && isNumber(cellData) ? (
        <Price price={cellData} isBold={boldAllPrices}/>
      ) : (
        <Text centered={isCenteredText} isFullWidthCell={isFullWidthCell} isBold={boldText} space={spaceCells}>
          {shouldUseTranslate ? t(`${cellData || ''}`.toLowerCase()) : cellData}
        </Text>
      )}
    </ContentCell>
  );
};

export default CellRenderer;
